import posthog from 'posthog-js';
import { getRuntimeConfig } from './runtime-config';

/**
 * Product analytics for the staff app, behind one thin wrapper so pages never
 * import the vendor SDK. Everything here is a no-op until `init()` finds a
 * project key in runtime config, which keeps `vite dev` and tests silent
 * unless someone opts in.
 *
 * What is sent: page views (on every route change), the signed-in viewer's
 * id, role and email, and any named events pages choose to capture. Nothing
 * about clients or event content unless a page passes it explicitly.
 */

/** PostHog's US cloud, the project's home unless config says otherwise. */
const DEFAULT_HOST = 'https://us.i.posthog.com';

let ready = false;

/**
 * Starts the SDK once. Safe to call more than once and with no key.
 *
 * @returns True when analytics is live for this page load.
 */
export function initAnalytics(): boolean {
  if (ready) return true;
  const key = getRuntimeConfig('POSTHOG_KEY');
  if (!key) return false;
  posthog.init(key, {
    api_host: getRuntimeConfig('POSTHOG_HOST') ?? DEFAULT_HOST,
    // Page views are captured by the router hook below, once per route
    // change, rather than once per full page load.
    capture_pageview: false,
    capture_pageleave: true,
    // Autocapture uncaught errors and unhandled promise rejections as
    // $exception events, so client crashes land in Error Tracking beside the
    // API's server-side exceptions.
    capture_exceptions: true,
    // Only signed-in staff use this tool; no anonymous profiles to build.
    person_profiles: 'identified_only',
  });
  ready = true;
  return true;
}

/** The viewer facts worth attaching to a person profile. */
export interface AnalyticsViewer {
  readonly _id: string;
  readonly email?: string | undefined;
  readonly role?: string | undefined;
  readonly firstName?: string | undefined;
  readonly lastName?: string | undefined;
}

/**
 * Ties events to the signed-in staff member.
 *
 * @param viewer - The identity from `/users/me`.
 */
export function identifyViewer(viewer: AnalyticsViewer): void {
  if (!ready) return;
  posthog.identify(viewer._id, {
    email: viewer.email,
    role: viewer.role,
    name: [viewer.firstName, viewer.lastName].filter(Boolean).join(' ') || undefined,
  });
}

/**
 * Records a route change.
 *
 * @param path - The pathname plus search, as the router sees it.
 */
export function trackPageView(path: string): void {
  if (!ready) return;
  posthog.capture('$pageview', { $current_url: `${window.location.origin}${path}` });
}

/**
 * Records a named product event. Name things by what the person did
 * ("attachment_uploaded"), not by the component that did it.
 *
 * @param event - Snake-case event name.
 * @param properties - Small, non-sensitive context.
 */
export function trackEvent(event: string, properties?: Record<string, string | number | boolean | undefined>): void {
  if (!ready) return;
  posthog.capture(event, properties);
}

/** Forgets the person on sign-out so the next sign-in starts clean. */
export function resetAnalytics(): void {
  if (!ready) return;
  posthog.reset();
}
